import React, { useState } from 'react';
import { Bot, FileText, Brain, Workflow, ArrowRight, Building2, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Service {
  icon: React.ReactNode;
  title: string;
  description: string;
  publicSector: {
    examples: string[];
    contractValue: string;
  };
  privateSector: {
    examples: string[];
    pricing: string;
  };
  deployment: string;
  automation: string;
  bgGradient: string;
  iconColor: string;
}

const services: Service[] = [
  {
    icon: <Bot className="w-12 h-12" />,
    title: 'Robotic Process Automation (RPA)',
    description: 'Automate repetitive tasks and processes with intelligent software robots that work 24/7.',
    publicSector: {
      examples: ['Claims processing (DWP)', 'Data migration (NHS)', 'Invoice automation (Councils)'],
      contractValue: '£62k - £3M'
    },
    privateSector: {
      examples: ['Order processing', 'Invoice reconciliation', 'Customer onboarding'],
      pricing: 'From £500/bot/month'
    },
    deployment: '6-12 weeks',
    automation: '90-99% autonomous',
    bgGradient: 'from-blue-500/10 to-blue-600/10',
    iconColor: 'text-blue-400'
  },
  {
    icon: <FileText className="w-12 h-12" />,
    title: 'Document Processing & AI OCR',
    description: 'Extract and process data from documents automatically using advanced AI and machine learning.',
    publicSector: {
      examples: ['Medical records (NHS)', 'Planning applications', 'Handwritten forms'],
      contractValue: '£30k - £150k'
    },
    privateSector: {
      examples: ['Contract analysis', 'Receipt processing', 'KYC documentation'],
      pricing: '£0.05-£0.50 per document'
    },
    deployment: '4-6 weeks',
    automation: '80-95% autonomous',
    bgGradient: 'from-teal-500/10 to-teal-600/10',
    iconColor: 'text-teal-400'
  },
  {
    icon: <Brain className="w-12 h-12" />,
    title: 'Agentic AI & Intelligent Assistants',
    description: 'Deploy AI agents that understand context, make decisions, and assist your teams autonomously.',
    publicSector: {
      examples: ['Citizen service assistance', 'Policy guidance bots', 'ATRS-compliant AI'],
      contractValue: '£100k - £5M+'
    },
    privateSector: {
      examples: ['Customer support AI', 'Sales assistants', 'Internal help desk'],
      pricing: 'Custom enterprise pricing'
    },
    deployment: '8-16 weeks',
    automation: '60-80% with oversight',
    bgGradient: 'from-purple-500/10 to-purple-600/10',
    iconColor: 'text-purple-400'
  },
  {
    icon: <Workflow className="w-12 h-12" />,
    title: 'Workflow Automation',
    description: 'Streamline business processes and connect systems with intelligent workflow automation.',
    publicSector: {
      examples: ['Tender compliance checking', 'Approval routing', 'Contract monitoring'],
      contractValue: '£10k - £300k'
    },
    privateSector: {
      examples: ['Lead nurturing', 'Employee onboarding', 'Inventory management'],
      pricing: 'From £2k/month'
    },
    deployment: '2-6 weeks',
    automation: '80-90% autonomous',
    bgGradient: 'from-green-500/10 to-green-600/10',
    iconColor: 'text-green-400'
  }
];

export default function UnifiedServices() {
  const [activeSector, setActiveSector] = useState<'all' | 'public' | 'private'>('all');

  return (
    <div className="py-24 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI Automation Services
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Comprehensive solutions for government, healthcare, councils, and private enterprises.
            All services include UK data residency and enterprise-grade security.
          </p>

          <div className="inline-flex items-center bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-1">
            <button
              onClick={() => setActiveSector('all')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                activeSector === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All Services
            </button>
            <button
              onClick={() => setActiveSector('public')}
              className={`px-6 py-2 rounded-md font-medium transition-all flex items-center space-x-2 ${
                activeSector === 'public'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Building2 className="w-4 h-4" />
              <span>Public Sector</span>
            </button>
            <button
              onClick={() => setActiveSector('private')}
              className={`px-6 py-2 rounded-md font-medium transition-all flex items-center space-x-2 ${
                activeSector === 'private'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              <span>Private Sector</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 hover:border-gray-600/50 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${service.bgGradient} rounded-2xl opacity-50 group-hover:opacity-70 transition-opacity`} />

              <div className="relative">
                <div className={`${service.iconColor} mb-6`}>
                  {service.icon}
                </div>

                <h3 className="text-2xl font-bold text-white mb-3">
                  {service.title}
                </h3>

                <p className="text-gray-300 mb-6">
                  {service.description}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Automation Level</p>
                    <p className="text-sm font-semibold text-white">{service.automation}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Deployment</p>
                    <p className="text-sm font-semibold text-white">{service.deployment}</p>
                  </div>
                </div>

                {(activeSector === 'all' || activeSector === 'public') && (
                  <div className="mb-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Building2 className="w-4 h-4 text-blue-400" />
                      <h4 className="font-semibold text-blue-300">Public Sector</h4>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Contract Value: <span className="text-blue-300 font-semibold">{service.publicSector.contractValue}</span></p>
                    <ul className="space-y-1">
                      {service.publicSector.examples.map((example, i) => (
                        <li key={i} className="flex items-start space-x-2 text-sm text-gray-300">
                          <span className="text-blue-400">•</span>
                          <span>{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(activeSector === 'all' || activeSector === 'private') && (
                  <div className="mb-6 bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Briefcase className="w-4 h-4 text-teal-400" />
                      <h4 className="font-semibold text-teal-300">Private Sector</h4>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Pricing: <span className="text-teal-300 font-semibold">{service.privateSector.pricing}</span></p>
                    <ul className="space-y-1">
                      {service.privateSector.examples.map((example, i) => (
                        <li key={i} className="flex items-start space-x-2 text-sm text-gray-300">
                          <span className="text-teal-400">•</span>
                          <span>{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Link
                  to="/contact"
                  className={`inline-flex items-center space-x-2 ${service.iconColor} hover:underline font-medium text-sm group-hover:translate-x-1 transition-transform`}
                >
                  <span>Request Quote</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            All Services Include
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <p className="font-semibold text-teal-400 mb-2">Security & Compliance</p>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Cyber Essentials Plus</li>
                <li>• ISO 27001 certified</li>
                <li>• UK-only data storage</li>
                <li>• GDPR native</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-blue-400 mb-2">Public Sector Ready</p>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• G-Cloud 14 framework</li>
                <li>• Social value commitments</li>
                <li>• ATRS compliance</li>
                <li>• Procurement support</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-purple-400 mb-2">Enterprise Features</p>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• 24/7 monitoring</li>
                <li>• Dedicated support</li>
                <li>• Custom integrations</li>
                <li>• SLA guarantees</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-green-400 mb-2">Delivery Model</p>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Free consultation</li>
                <li>• Pilot/POC options</li>
                <li>• Phased deployment</li>
                <li>• Ongoing optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
